import { useCallback, useEffect, useRef } from 'react';

import { useInteractiveObjects } from '@spazious/dollhouse-engine';
import { useStore } from '@spazious/dollhouse-store';

export const CollisionControls: React.FC = () => {
  const [interactiveObjects] = useInteractiveObjects();
  // const { set: setFsetup, updateTree } = useFsetupStore();
  const { graph, world, history, settings, staging } = useStore();
  const initialised = useRef(false);

  // flush collisions when a change is committed in staging
  useEffect(() => {
    const updateHandler = () => {
      graph.flushCollisions();
    };

    staging.on('commit', updateHandler);
    return () => {
      staging.off('commit', updateHandler);
    };
  }, [staging, graph]);

  // handle intial collisions when scene is added to the world
  useEffect(() => {
    const onWorldReady = () => {
      if (settings.enableCollisions) {
        world.octree.updateObjects(interactiveObjects);
        graph.checkCollisions();
      }
    };

    if (world && interactiveObjects?.length && !initialised.current) {
      initialised.current = true;
    }

    if (world) {
      world.on('ready', onWorldReady);
      return () => {
        world.off('ready', onWorldReady);
      };
    }
  }, [world, interactiveObjects, settings.enableCollisions, graph]);

  const updateOctreeAndCollisions = useCallback(() => {
    if (settings.enableCollisions) {
      world.octree.updateObjects(interactiveObjects);
      // timeout because the octree graph internally updated after a timeout
      // TODO - find a better way to handle this instead of timeout
      setTimeout(() => {
        graph.checkCollisions();
      }, 0);
    }
  }, [settings.enableCollisions, world, interactiveObjects, graph]);

  // when objects added/removed or collisions enabled,
  // update octree and then check collisons
  useEffect(() => updateOctreeAndCollisions(), [updateOctreeAndCollisions]);

  // when history changes, wait for graph update
  // and then update octree and then check collisons
  useEffect(() => {
    const onGraphUpdate = () => {
      updateOctreeAndCollisions();
    };

    const onHistoryUpdate = () => {
      graph.once('update', onGraphUpdate);
    };
    history.on('change', onHistoryUpdate);
    return () => {
      history.off('change', onHistoryUpdate);
    };
  }, [world, history, graph, updateOctreeAndCollisions]);

  // when collisions disabled, flush collisions
  useEffect(() => {
    if (!settings.enableCollisions) {
      graph.flushCollisions();
    }
  }, [graph, settings.enableCollisions]);

  return null;
};
